import { Edge, Node } from '@xyflow/react';
import { v7 as uuid } from 'uuid';

import {
  initialNodeDataMap,
  NODE_TYPES_MAP,
} from '@/src/modules/AutomationModuleNew/constants/flows';
import {
  JourneyConnectionType,
  JourneyNodeTypes,
  JourneySchema,
  TriggerNodeData,
} from '@/src/modules/AutomationModuleNew/types/flowsTypes';

const getNumberId = (items: { id: string }[]) => {
  const existingIds = new Set(
    items
      .map((n) => {
        const match = n.id.match(/\d+/);
        return match ? parseInt(match[0], 10) : NaN;
      })
      .filter(Number.isFinite)
  );

  let counter = 1;
  while (existingIds.has(counter)) {
    counter++;
  }

  return counter;
};

export const getNextNodeId = (): string => {
  const nextId = uuid();
  return nextId;
};

export const getNextEdgeId = (existingEdges: Edge[]): string => {
  const nextId = getNumberId(existingEdges);
  return `connection${nextId}`;
};

export const getMainNodeType = (type: JourneyNodeTypes) => {
  for (const [nodeType, types] of Object.entries(NODE_TYPES_MAP)) {
    if (types.includes(type)) return nodeType;
  }
  return 'unknown';
};

export const findRootNodeId = (
  nodes: Node[],
  connections: JourneyConnectionType[]
): string | null => {
  const nodesWithIncomingConnections = new Set(connections.map((c) => c.to));

  const rootNode = nodes.find((node) => !nodesWithIncomingConnections.has(node.id));

  return rootNode?.id || null;
};

export const findRootNodeIdByTriggerId = (
  nodeTriggerId: string,
  connections: JourneyConnectionType[]
): string | null => {
  const rootNodeId = connections.find((connection) => connection.from === nodeTriggerId)?.to;
  return rootNodeId || null;
};

export const getInitialNodeData = (type: JourneyNodeTypes) => {
  return initialNodeDataMap[type] || null;
};

interface SplitResult {
  triggers: TriggerNodeData[];
  journeySchema: JourneySchema | null;
}

export const splitTriggerAndJourneySchema = (schemas: JourneySchema): SplitResult => {
  const triggers: TriggerNodeData[] = [];
  const nodes: JourneySchema['nodes'] = [];

  for (const node of schemas.nodes) {
    const mainType = getMainNodeType(node.type);

    if (mainType === 'trigger') {
      triggers.push(node as TriggerNodeData);
    } else {
      nodes.push(node);
    }
  }

  const connections: JourneyConnectionType[] = schemas.connections.filter((connection) => {
    if (triggers.length === 0) return schemas.connections;
    return triggers.some((trigger) => trigger.id !== connection.from);
  });

  const journeySchema: JourneySchema = {
    nodes,
    connections,
    root: findRootNodeIdByTriggerId(triggers[0]?.id, schemas.connections) ?? '',
  };

  return { triggers, journeySchema };
};

export const findIfHasTriggers = (nodes: Node[]): boolean => {
  return nodes.some((node) => {
    const mainType = node.type;
    return mainType === 'trigger';
  });
};
